import React from "react";
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Changelog } from "@components/Changelog";
import { Link as GatsbyLink } from "gatsby";
import { OdysseyUser, getCurrentLesson } from "@utils";
import { noop } from "lodash";
import { useLocalStorage } from "react-use";

type ConfirmVersionProps = {
  changelog?: readonly string[] | null;
  course: {
    lessons: readonly {
      slug: string;
      tasks: readonly {
        id: string;
      }[];
    }[];
  } & Pick<Queries.Course, "title" | "slug" | "version">;
  user: OdysseyUser;
};

export const ConfirmVersion = ({
  user,
  changelog,
  course,
}: ConfirmVersionProps) => {
  const [hasAcknowledged, setHasAcknowledged] = useLocalStorage(
    `${course.slug}:version`,
    false
  );

  const acknowledge = () => setHasAcknowledged(true);

  const currentLesson = getCurrentLesson(course.lessons, user.tasks ?? []);

  return (
    <Modal isOpen={hasAcknowledged !== true} onClose={noop}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={{ base: "xl", md: "2xl" }}>
          New course version ✨
        </ModalHeader>
        <ModalBody>
          <Text>
            We&apos;ve updated this course since you were last here—but
            don&apos;t worry, you won&apos;t lose any of your progress.
          </Text>
          {changelog && (
            <>
              <Heading mt="4" mb="2" size="md">
                Here&apos;s what changed:
              </Heading>
              <Changelog changes={changelog} />
            </>
          )}
          <Text mt="4">
            You have progress on{" "}
            <strong>
              {course.title} {course.version}
            </strong>
            . Do you want to continue on the old version or start the new
            version from the beginning?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            mr="3"
            onClick={acknowledge}
            as={GatsbyLink}
            to={`/${(currentLesson || course.lessons[0]).slug}`}
          >
            Continue old version
          </Button>
          <Button colorScheme="indigo" onClick={acknowledge}>
            Start latest version
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
